import React, { useRef, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import * as d3 from 'd3';
import FiltersContext from '../../contexts/FiltersContext';

function MapLegend({ width, height, noDataColor }) {
  const legendRef = useRef(null);
  const { childRace, displayBy } = useContext(FiltersContext);

  const colors = [
    '#ffffff', '#F8BAB6', '#F2756D',
    '#7FA4C7', '#8D7991', '#9B4E5B',
    '#004990', '#22386D', '#44274A',
  ];

  useEffect(() => {
    if (width === 0) {
      return; // wait for responsive component before doing all the work
    }

    const svg = d3.select(legendRef.current)
      .html(null) // clear
      .append('svg')
      .attr('width', width)
      .attr('height', height);

    svg.append('g')

      .attr('width', width)
      .attr('height', height)
      .style('fill', '#ddd');

    const oneRow = childRace === 'All';

    const gridHorizontalItemsCount = 3; // grid size x size
    const gridVerticalItemsCount = oneRow ? 1 : 3; // grid n x m
    const gridPaddingTop = 20; // to move down and to the right to make space for the labels
    const gridPaddingLeft = 40;
    const gridWidth = width - (gridPaddingTop + gridPaddingLeft);
    const rectangleWidth = gridWidth / gridHorizontalItemsCount;
    const gridHeight = ((height - (gridPaddingTop * 2)) / 4) * 3; // take 3/4
    const rectangleHeight = gridHeight / gridHorizontalItemsCount;

    const novalueG = svg.append('g')
      .attr('width', gridWidth) // full width to make space for the label
      .attr('height', rectangleHeight)
      .attr('transform', `translate(${gridPaddingLeft},${gridPaddingTop + (rectangleHeight * gridVerticalItemsCount)})`);

    novalueG.append('rect')
      .attr('width', rectangleWidth)
      .attr('height', rectangleHeight)
      .attr('x', 0)
      .attr('y', rectangleHeight / 2)
      .style('fill', noDataColor);

    novalueG
      .append('text')
      .attr('class', 'legend-label')
      .attr('x', rectangleWidth + 10)
      // .attr('y', (d) => (rectangleHeight))
      .attr('y', () => (rectangleHeight))
      .attr('text-anchor', 'center')
      .attr('alignment-baseline', 'middle')
      .style('font', '1.1em "ff-scala-sans-pro", Arial, sans-serif')
      .text('No data');

    const g = svg.append('g').attr('transform', `translate(${gridPaddingLeft},${gridPaddingTop})`); // shift group down and right

    d3.cross(d3.range(gridHorizontalItemsCount), d3.range(gridVerticalItemsCount)).forEach(([i, j]) => {
      g.append('rect')
        .attr('width', rectangleWidth)
        .attr('height', rectangleHeight)
        .attr('x', i * rectangleWidth)
        .attr('y', j * rectangleHeight)
        .attr('fill', colors[j * gridHorizontalItemsCount + i]);
    });

    const markerBoxHeight = 10;
    const markerBoxWidth = 10;
    const refX = markerBoxWidth / 2;
    const refY = markerBoxHeight / 2;

    svg
      .append('defs')
      .append('marker')
      .attr('id', 'arrow')
      .attr('viewBox', [0, 0, markerBoxWidth, markerBoxHeight])
      .attr('refX', refX)
      .attr('refY', refY)
      .attr('markerWidth', markerBoxWidth)
      .attr('markerHeight', markerBoxHeight)
      .attr('orient', 'auto-start-reverse')
      .append('path')
      .attr('d', d3.line()([[0, 0], [0, 10], [10, 5]]))
      .attr('stroke', 'black');

    // top horizontal line
    svg.append('path')
      .attr('d', d3.line()([[gridPaddingLeft, gridPaddingTop], [gridWidth + gridPaddingLeft + (markerBoxWidth / 2), gridPaddingTop]]))
      .attr('stroke', 'black')
      .attr('marker-end', 'url(#arrow)');

    if (!oneRow) {
      // left vertical line
      svg.append('path')
        .attr('d', d3.line()([[gridPaddingLeft, gridPaddingTop], [gridPaddingLeft, gridHeight + gridPaddingTop + (markerBoxHeight / 2)]]))
        .attr('stroke', 'black')
        .attr('marker-end', 'url(#arrow)');
    }

    // top label
    svg.append('foreignObject')
      .attr('x', gridPaddingTop)
      .attr('y', 0)
      .attr('height', gridPaddingTop)
      .attr('width', gridWidth)
      .append('xhtml:div')
      .style('height', gridPaddingTop)
      .style('width', `${gridWidth}px`)
      .style('display', 'flex')
      .style('justify-content', 'center')
      .style('align-items', 'flex-end')
      .style('text-align', 'center')
      .style('font-family', '"ff-scala-sans-pro", Arial, sans-serif')
      .style('font-size', '1.1em')
      // .style('font-weight', 'bold')
      .html(`% ${displayBy.toLowerCase()} decrease`);

    if (!oneRow) {
      // left label
      svg.append('foreignObject')
        .attr('x', gridPaddingLeft)
        .attr('y', gridPaddingTop)
        .attr('height', gridPaddingLeft)
        .attr('width', gridHeight)
        .attr('transform', `rotate(90, ${gridPaddingLeft}, ${gridPaddingTop})`)
        .append('xhtml:div')
        .style('height', gridPaddingLeft)
        .style('width', `${gridHeight}px`)
        .style('display', 'flex')
        .style('justify-content', 'center')
        .style('align-items', 'flex-end')
        .style('text-align', 'center')
        .style('font-family', '"ff-scala-sans-pro", Arial, sans-serif')
        .style('font-size', '1.1em')
        // .style('font-weight', 'bold')
        .html(`% ${childRace !== 'All' ? ` children that are ${childRace}` : 'children'}`);
    }
  }, [displayBy, childRace, width]);

  return <div ref={legendRef} style={{ width, height }} />;
}

MapLegend.propTypes = {
  width: PropTypes.number,
  height: PropTypes.number,
  noDataColor: PropTypes.string,
};

MapLegend.defaultProps = {
  width: 200,
  height: 200,
  noDataColor: '#ccc',
};

export default MapLegend;
